import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import Sidebar from './Sidebar';
import LoadingSpinner from './LoadingSpinner';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const NewClientPage = () => {
  const navigate = useNavigate();
  const { user, clinic, loading, isAuthenticated } = useAuth();
  const [clinicId, setClinicId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Client form state
  const [clientForm, setClientForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: ''
  });

  // Pets state
  const [pets, setPets] = useState([]);
  const [showPetForm, setShowPetForm] = useState(false);
  const [petForm, setPetForm] = useState({
    name: '',
    species: '',
    species_other: '',
    breed: '',
    birth_date: '',
    weight: ''
  });

  // Toast notification state
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const toastTimeoutRef = useRef(null);

  // Redirect if not authenticated
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login');
    }
  }, [loading, isAuthenticated, navigate]);

  // Resolve clinic ID (supports both clinic owners and vet employees)
  useEffect(() => {
    if (clinic?.id) {
      setClinicId(clinic.id);
      return;
    }
    if (user && !loading) {
      supabase
        .from('vets')
        .select('clinic_id')
        .eq('user_id', user.id)
        .single()
        .then(({ data }) => {
          if (data?.clinic_id) setClinicId(data.clinic_id);
        });
    }
  }, [user, clinic, loading]);

  // Cleanup toast timeout on unmount
  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    };
  }, []);

  const showToast = (message, type = 'success') => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToast({ show: true, message, type });
    toastTimeoutRef.current = setTimeout(() => {
      setToast({ show: false, message: '', type: 'success' });
    }, 4000);
  };

  const handleClientFormChange = (e) => {
    const { name, value } = e.target;
    setClientForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePetFormChange = (e) => {
    const { name, value } = e.target;
    setPetForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const addPet = () => {
    if (petForm.name.trim() && (petForm.species.trim() || (petForm.species === 'other' && petForm.species_other.trim()))) {
      const finalSpecies = petForm.species === 'other' ? petForm.species_other : petForm.species;
      setPets(prev => [...prev, { ...petForm, species: finalSpecies, id: Date.now() }]);
      setPetForm({
        name: '',
        species: '',
        species_other: '',
        breed: '',
        birth_date: '',
        weight: ''
      });
      setShowPetForm(false);
      showToast(`Το κατοικίδιο "${petForm.name}" προστέθηκε!`, 'success');
    } else {
      showToast('Παρακαλώ συμπληρώστε τουλάχιστον το όνομα και το είδος του κατοικιδίου.', 'error');
    }
  };

  const removePet = (petId) => {
    const petToRemove = pets.find(pet => pet.id === petId);
    setPets(prev => prev.filter(pet => pet.id !== petId));
    showToast(`Το κατοικίδιο "${petToRemove.name}" αφαιρέθηκε.`, 'info');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!clientForm.first_name.trim() || !clientForm.last_name.trim()) {
      showToast('Παρακαλώ συμπληρώστε τουλάχιστον το όνομα και επώνυμο του πελάτη.', 'error');
      return;
    }

    if (!clinicId) {
      showToast('Δεν βρέθηκε κλινική. Παρακαλώ δοκιμάστε ξανά.', 'error');
      return;
    }

    setSubmitting(true);

    try {
      // Insert client
      const { data: clientData, error: clientError } = await supabase
        .from('owners')
        .insert({
          first_name: clientForm.first_name.trim(),
          last_name: clientForm.last_name.trim(),
          email: clientForm.email.trim() || null,
          phone: clientForm.phone.trim() || null,
          address: clientForm.address.trim() || null,
          clinic_id: clinicId
        })
        .select()
        .single();

      if (clientError) throw clientError;

      // Insert pets if any
      if (pets.length > 0) {
        const petsToInsert = pets.map(pet => ({
          name: pet.name.trim(),
          species: pet.species.trim(),
          breed: pet.breed.trim() || null,
          birth_date: pet.birth_date || null,
          weight: pet.weight ? parseFloat(pet.weight) : null,
          owner_id: clientData.id
        }));

        const { error: petsError } = await supabase
          .from('pets')
          .insert(petsToInsert);

        if (petsError) {
          // Rollback: delete the owner we just created since pets failed
          await supabase.from('owners').delete().eq('id', clientData.id);
          throw petsError;
        }
      }

      showToast(`Ο πελάτης "${clientForm.first_name} ${clientForm.last_name}" προστέθηκε επιτυχώς!`, 'success');

      setTimeout(() => {
        navigate('/clients');
      }, 1500);

    } catch (error) {
      console.error('Error adding client:', error);
      showToast('Σφάλμα κατά την προσθήκη του πελάτη. Παρακαλώ δοκιμάστε ξανά.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/clients');
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="min-h-screen text-white flex flex-col lg:flex-row" style={{ backgroundColor: '#1A202C', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, background: 'radial-gradient(800px 800px at 10% -10%, rgba(16,185,129,0.12), transparent), radial-gradient(800px 800px at 110% 10%, rgba(59,130,246,0.08), transparent)' }} className="pointer-events-none" />

      {/* Toast Notification */}
      {toast.show && (
        <div className="fixed top-4 right-4 left-4 sm:left-auto z-50">
          <div
            className="flex items-center px-5 py-4 rounded-xl border max-w-sm ml-auto transition-all duration-300"
            style={{
              background: 'linear-gradient(135deg, rgba(17,24,39,0.95) 0%, rgba(31,41,55,0.95) 100%)',
              backdropFilter: 'blur(10px)',
              borderColor: toast.type === 'success' ? '#10B981' : toast.type === 'error' ? '#EF4444' : '#3B82F6',
              boxShadow: `0 10px 25px rgba(0,0,0,0.3), 0 0 20px ${
                toast.type === 'success' ? 'rgba(16,185,129,0.2)' :
                toast.type === 'error' ? 'rgba(239,68,68,0.2)' : 'rgba(59,130,246,0.2)'
              }`
            }}
          >
            {toast.type === 'success' && (
              <div className="w-8 h-8 rounded-full flex items-center justify-center mr-3" style={{ background: 'rgba(16,185,129,0.2)' }}>
                <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            )}
            {toast.type === 'error' && (
              <div className="w-8 h-8 rounded-full flex items-center justify-center mr-3" style={{ background: 'rgba(239,68,68,0.2)' }}>
                <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
            )}
            {toast.type === 'info' && (
              <div className="w-8 h-8 rounded-full flex items-center justify-center mr-3" style={{ background: 'rgba(59,130,246,0.2)' }}>
                <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            )}
            <span className="text-white font-medium flex-1">{toast.message}</span>
            <button
              onClick={() => setToast({ show: false, message: '', type: 'success' })}
              className="ml-3 text-gray-400 hover:text-white transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Sidebar */}
      <Sidebar isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen} />

      {/* Main Content */}
      <div className="flex-1 p-4 lg:p-8">
        {/* Header */}
        <div className="mb-6 lg:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-white mb-1">Νέος Πελάτης</h1>
              <p className="text-gray-400 text-sm lg:text-base">
                Προσθέστε νέο πελάτη και τα κατοικίδιά του
              </p>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-xl" style={{ background: 'linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(6,182,212,0.15) 100%)', border: '1px solid rgba(16,185,129,0.3)' }}>
              <svg className="w-5 h-5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
              <span className="text-emerald-400 font-medium text-sm">Νέα εγγραφή</span>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Client Form */}
          <div className="mb-8">
            <div className="rounded-lg overflow-hidden" style={{ backgroundColor: '#2D3748' }}>
              <div className="px-6 py-4 border-b" style={{ borderColor: '#4A5568' }}>
                <h3 className="text-lg font-semibold text-white">Στοιχεία Πελάτη</h3>
              </div>

              <div className="p-6">
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* First Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Όνομα *
                      </label>
                      <input
                        type="text"
                        name="first_name"
                        value={clientForm.first_name}
                        onChange={handleClientFormChange}
                        required
                        className="w-full px-3 py-2 rounded-lg border text-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 focus:outline-none transition-colors"
                        style={{ backgroundColor: '#4A5568', borderColor: '#718096' }}
                        placeholder="Εισάγετε το όνομα"
                      />
                    </div>

                    {/* Last Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Επώνυμο *
                      </label>
                      <input
                        type="text"
                        name="last_name"
                        value={clientForm.last_name}
                        onChange={handleClientFormChange}
                        required
                        className="w-full px-3 py-2 rounded-lg border text-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 focus:outline-none transition-colors"
                        style={{ backgroundColor: '#4A5568', borderColor: '#718096' }}
                        placeholder="Εισάγετε το επώνυμο"
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={clientForm.email}
                        onChange={handleClientFormChange}
                        className="w-full px-3 py-2 rounded-lg border text-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 focus:outline-none transition-colors"
                        style={{ backgroundColor: '#4A5568', borderColor: '#718096' }}
                        placeholder="example@email.com"
                      />
                    </div>

                    {/* Phone */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Τηλέφωνο
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={clientForm.phone}
                        onChange={handleClientFormChange}
                        className="w-full px-3 py-2 rounded-lg border text-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 focus:outline-none transition-colors"
                        style={{ backgroundColor: '#4A5568', borderColor: '#718096' }}
                        placeholder="2101234567"
                      />
                    </div>

                    {/* Address */}
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Διεύθυνση
                      </label>
                      <input
                        type="text"
                        name="address"
                        value={clientForm.address}
                        onChange={handleClientFormChange}
                        className="w-full px-3 py-2 rounded-lg border text-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 focus:outline-none transition-colors"
                        style={{ backgroundColor: '#4A5568', borderColor: '#718096' }}
                        placeholder="Εισάγετε τη διεύθυνση"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Pets Section */}
          <div className="mb-8">
            <div className="rounded-lg overflow-hidden" style={{ backgroundColor: '#2D3748' }}>
              <div className="px-6 py-4 border-b" style={{ borderColor: '#4A5568' }}>
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-white">Κατοικίδια</h3>
                  <button
                    type="button"
                    onClick={() => setShowPetForm(true)}
                    className="px-4 py-2 rounded-lg font-medium text-white transition-colors hover:opacity-90"
                    style={{ backgroundColor: '#10B981' }}
                  >
                    + Προσθήκη Κατοικιδίου
                  </button>
                </div>
              </div>

              <div className="p-6">
                {/* Existing Pets List */}
                {pets.length > 0 ? (
                  <div className="mb-6">
                    <h4 className="text-md font-medium text-white mb-4">Προστιθέμενα κατοικίδια:</h4>
                    <div className="space-y-3">
                      {pets.map((pet) => (
                        <div key={pet.id} className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: '#4A5568' }}>
                          <div>
                            <span className="text-white font-medium">{pet.name}</span>
                            <span className="text-gray-300 ml-2">({pet.species})</span>
                            {pet.breed && <span className="text-gray-400 ml-2">- {pet.breed}</span>}
                          </div>
                          <button
                            type="button"
                            onClick={() => removePet(pet.id)}
                            className="text-red-400 hover:text-red-300 transition-colors"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : !showPetForm && (
                  <div className="text-center py-8">
                    <svg className="w-12 h-12 text-gray-600 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 12H4m8-8v16" />
                    </svg>
                    <p className="text-gray-500 text-sm">Δεν έχουν προστεθεί κατοικίδια ακόμα</p>
                    <p className="text-gray-600 text-xs mt-1">Πατήστε «+ Προσθήκη Κατοικιδίου» για να ξεκινήσετε</p>
                  </div>
                )}

                {/* Add Pet Form */}
                {showPetForm && (
                  <div className="border rounded-lg p-4" style={{ borderColor: '#4A5568' }}>
                    <h4 className="text-md font-medium text-white mb-4">Προσθήκη νέου κατοικιδίου</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Pet Name */}
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Όνομα Κατοικιδίου *
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={petForm.name}
                          onChange={handlePetFormChange}
                          className="w-full px-3 py-2 rounded-lg border text-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 focus:outline-none transition-colors"
                          style={{ backgroundColor: '#4A5568', borderColor: '#718096' }}
                          placeholder="Εισάγετε το όνομα"
                        />
                      </div>

                      {/* Species */}
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Είδος *
                        </label>
                        <select
                          name="species"
                          value={petForm.species}
                          onChange={handlePetFormChange}
                          className="w-full px-3 py-2 rounded-lg border text-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 focus:outline-none transition-colors"
                          style={{ backgroundColor: '#4A5568', borderColor: '#718096' }}
                        >
                          <option value="">Επιλέξτε είδος</option>
                          <option value="Σκύλος">Σκύλος</option>
                          <option value="Γάτα">Γάτα</option>
                          <option value="other">Άλλο</option>
                        </select>
                      </div>

                      {/* Species Other - Only show when "other" is selected */}
                      {petForm.species === 'other' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Είδος (προσαρμοσμένο) *
                          </label>
                          <input
                            type="text"
                            name="species_other"
                            value={petForm.species_other}
                            onChange={handlePetFormChange}
                            className="w-full px-3 py-2 rounded-lg border text-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 focus:outline-none transition-colors"
                            style={{ backgroundColor: '#4A5568', borderColor: '#718096' }}
                            placeholder="π.χ. Κουνέλι, Πουλί, Χάμστερ"
                          />
                        </div>
                      )}

                      {/* Breed */}
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Φυλή
                        </label>
                        <input
                          type="text"
                          name="breed"
                          value={petForm.breed}
                          onChange={handlePetFormChange}
                          className="w-full px-3 py-2 rounded-lg border text-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 focus:outline-none transition-colors"
                          style={{ backgroundColor: '#4A5568', borderColor: '#718096' }}
                          placeholder="π.χ. Γερμανικός Ποιμενικός"
                        />
                      </div>

                      {/* Birth Date */}
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Ημερομηνία Γέννησης
                        </label>
                        <DatePicker
                          selected={petForm.birth_date ? new Date(petForm.birth_date + 'T00:00:00') : null}
                          onChange={(date) => {
                            if (date) {
                              const yyyy = date.getFullYear();
                              const mm = String(date.getMonth() + 1).padStart(2, '0');
                              const dd = String(date.getDate()).padStart(2, '0');
                              handlePetFormChange({ target: { name: 'birth_date', value: `${yyyy}-${mm}-${dd}` } });
                            } else {
                              handlePetFormChange({ target: { name: 'birth_date', value: '' } });
                            }
                          }}
                          maxDate={new Date()}
                          dateFormat="dd/MM/yyyy"
                          showMonthDropdown
                          showYearDropdown
                          dropdownMode="select"
                          scrollableYearDropdown
                          yearDropdownItemNumber={40}
                          isClearable
                          placeholderText="Επιλέξτε ημερομηνία"
                          className="w-full px-3 py-2 rounded-lg border text-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 focus:outline-none transition-colors"
                          calendarClassName="dark-datepicker"
                          wrapperClassName="w-full"
                        />
                      </div>

                      {/* Weight */}
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Βάρος (kg)
                        </label>
                        <input
                          type="number"
                          name="weight"
                          value={petForm.weight}
                          onChange={handlePetFormChange}
                          step="0.1"
                          min="0"
                          className="w-full px-3 py-2 rounded-lg border text-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 focus:outline-none transition-colors"
                          style={{ backgroundColor: '#4A5568', borderColor: '#718096' }}
                          placeholder="0.0"
                        />
                      </div>
                    </div>

                    {/* Pet Form Actions */}
                    <div className="flex justify-end space-x-3 mt-4">
                      <button
                        type="button"
                        onClick={() => setShowPetForm(false)}
                        className="px-4 py-2 rounded-lg font-medium text-gray-300 transition-colors hover:opacity-90"
                        style={{ backgroundColor: '#4A5568' }}
                      >
                        Ακύρωση
                      </button>
                      <button
                        type="button"
                        onClick={addPet}
                        className="px-4 py-2 rounded-lg font-medium text-white transition-colors hover:opacity-90"
                        style={{ backgroundColor: '#10B981' }}
                      >
                        Προσθήκη
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 sm:gap-4">
            <button
              type="button"
              onClick={handleCancel}
              className="px-6 py-3 rounded-lg font-medium text-gray-300 transition-colors hover:opacity-90"
              style={{ backgroundColor: '#4A5568' }}
            >
              Ακύρωση
            </button>
            <button
              type="submit"
              disabled={submitting || !clinicId}
              className="px-6 py-3 rounded-lg font-medium text-white transition-colors hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: '#10B981' }}
            >
              {submitting ? 'Προσθήκη...' : 'Προσθήκη Πελάτη'}
            </button>
          </div>
        </form>
      </div>

      {/* Mobile overlay */}
      {isSidebarOpen && (
        <div className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setIsSidebarOpen(false)} />
      )}
    </div>
  );
};

export default NewClientPage;
